<template>
    <Main>
        <box-loading v-if="isLoading"></box-loading>
        <form autocomplete="off" v-else>
            <div class="px-0 px-sm-3 pt-4">
                <InputField classes="mb-2 mb-sm-3" is-required :label-text="$t('member.fullName')">
                    <el-row :gutter="20">
                        <el-col :span="12">
                            <InputField type="vertical" is-required :label-text="$t('member.firstName')">
                                <InputText v-model="member.firstName" @keydown.native="onValidate('firstName')" @keyup.native="onValidate('firstName')"></InputText>
                                <template #error v-if="errorInfo.firstName">
                                    {{ errorInfo.firstName }}
                                </template>
                            </InputField>
                        </el-col>
                        <el-col :span="12">
                            <InputField type="vertical" :label-text="$t('member.lastName')">
                                <InputText v-model="member.lastName"></InputText>
                            </InputField>
                        </el-col>
                    </el-row>
                </InputField>
                <InputField classes="mb-2 mb-sm-3" :label-text="$t('member.fullname')">
                    <InputText autocomplete="off" :disabled="true" :value="fullname" readonly></InputText>
                </InputField>
                <InputField classes="mb-2 mb-sm-3" :label-text="$t('member.name_halfsize')">
                    <InputText autocomplete="off" :disabled="true" :value="nameHalfsize" readonly></InputText>
                </InputField>
                <InputField classes="mb-2 mb-sm-3" :label-text="$t('member.name_fullsize')">
                    <InputText autocomplete="off" :value="nameFullsize" :disabled="true" readonly></InputText>
                </InputField>
                <InputField classes="mb-2 mb-sm-3" is-required :label-text="$t('member.accountEmail')">
                    <InputText autocomplete="off" v-model="member.email" @keyup.native="onValidate('email')" @keydown.native="onValidate('email')" readonly></InputText>
                    <template #error v-if="errorInfo.email">
                        {{ errorInfo.email }}
                    </template>
                </InputField>
                <InputField v-if="isEdit" is-required :label-text="$t('pageLogin.password')">
                    <div class="input-group input-group__password" v-if="checkFormat">
                        <input
                            :type="isShowPassword ? 'text' : 'password'"
                            id="password"
                            :readonly="checkPassword"
                            v-model="member.password"
                            @keydown="onValidate('password')"
                            @keyup="onValidate('password')"
                            class="form-control"
                            autocomplete="new-password"
                            :class="{ 'input-valid': errorInfo.password }"
                        />
                        <span class="password-icon">
                            <i @click="isShowPassword = !isShowPassword" class="fas" :class="{ 'fa-eye': isShowPassword, 'fa-eye-slash': !isShowPassword }"></i>
                        </span>
                    </div>
                    <template v-else>
                        <Button classes="mb-2" v-if="checkSettingPassword" type="primary" @click="resetPassword">
                            {{ $t('button.reset') }}
                        </Button>
                        <Button v-else v-loading="isLoadingResendEmail" :disabled="isLoadingResendEmail" classes="mb-2" type="primary" @click="resendEmail">
                            <el-icon class="el-icon-s-promotion mr-2"></el-icon>
                            {{ $t('button.resendEmail') }}
                        </Button>
                    </template>
                    <template #error v-if="errorInfo.password">
                        {{ errorInfo.password }}
                    </template>
                </InputField>
                <InputField classes="mt-1" v-if="checkFormat && isEdit">
                    <div class="checkbox-common">
                        <label class="ckbox label-small" style="width: fit-content">
                            <input id="checkbox1" class="custom-control-input" v-model="checkPassword" @change="checkPasswordChange" type="checkbox" />
                            <span for="checkbox1" class="pl-4">
                                {{ $t('member.textPasswordDefault', { field: defaultPassword }) }}
                            </span>
                        </label>
                    </div>
                </InputField>
                <InputField classes="mb-sm-2 mb-2" is-required :label-text="$t('member.role')">
                    <RadioList v-model="member.typeRole" disabled :data-list="listRoles"></RadioList>
                </InputField>
                <InputField classes="mb-2 mb-sm-3" :label-text="$t('member.company')">
                    <InputText v-model="member.companyName" @keydown.native="onValidate('company')" @keyup.native="onValidate('company')"></InputText>
                    <template #error v-if="errorInfo.companyName">{{ errorInfo.companyName }}</template>
                </InputField>
                <InputField classes="mb-2 mb-sm-3" :label-text="$t('member.phone')">
                    <InputText
                        v-model="member.phone"
                        @keypress="onValidPhoneNumber"
                        @paste="onPasteValidPhoneNumber"
                        @keydown.native="onValidate('phone')"
                        @keyup.native="onValidate('phone')"
                    ></InputText>
                    <template #error v-if="errorInfo.phone">{{ errorInfo.phone }}</template>
                </InputField>
                <InputField classes="mb-2 mb-sm-3" :label-text="$t('member.note')">
                    <textarea class="form-control" @keydown="onValidate('note')" @keyup="onValidate('note')" v-model="member.note"></textarea>
                </InputField>
            </div>
            <div class="data-steps__title pt-4">
                <h3>{{ $t('settingField.fieldSetting') }}</h3>
            </div>
            <div class="px-0 px-sm-3 pt-4">
                <template v-for="(item, k) in listDataField">
                    <InputField classes="mb-2 mb-sm-3" :label-text="item.field_name" :key="k">
                        <template v-if="item.field_type === 'text'">
                            <InputText v-model="item.value" @keydown.native="onValidate('text_setting', item)" @blur.native="onValidate('text_setting', item)"></InputText>
                        </template>
                        <template v-if="item.field_type === 'number'">
                            <InputNumber v-model="item.value"></InputNumber>
                        </template>
                        <template v-if="item.field_type === 'date'">
                            <el-date-picker type="date" :editable="false" format="yyyy/MM/dd" value-format="yyyy/MM/dd " v-model="item.value"> </el-date-picker>
                        </template>
                        <template v-if="item.field_type === 'datetime'">
                            <el-date-picker type="datetime" :editable="false" format="yyyy/MM/dd HH:mm" value-format="yyyy/MM/dd HH:mm" v-model="item.value"> </el-date-picker>
                        </template>
                        <template #error v-if="item.field_type === 'text' && item.error_field_text">{{ item.error_field_text }} </template>
                    </InputField>
                </template>
            </div>
        </form>
        <template v-if="!isLoading" #bottomAction>
            <Button classes="mr-3" type="warning" @click="$router.push({ name: 'Home' })">
                {{ $t('button.cancel') }}
            </Button>
            <Button type="primary" @click="handleSaveData">
                {{ isEdit ? $t('button.save') : $t('button.register') }}
            </Button>
        </template>
    </Main>
</template>

<script>
import Main from '@/components/partials/layout/Main';
import InputField from '@/components/partials/InputField';
import InputText from '@/components/partials/customs/InputText';
import InputNumber from '@/components/partials/customs/InputNumber';
import RadioList from '@/components/partials/RadioList';
import { mapState } from 'vuex';
import { validMaxLength, validRequired, validDuplicator, validPassword, validEmail, validNumber } from '@/utils';
import Button from '@/components/partials/Button';
import BoxLoading from '@/components/partials/BoxLoading';

export default {
    components: {
        BoxLoading,
        Button,
        InputField,
        Main,
        InputText,
        RadioList,
        InputNumber
    },
    data() {
        return {
            member: {
                id: '',
                email: '',
                password: '',
                firstName: '',
                lastName: '',
                companyName: '',
                phone: '',
                settingPassword: false,
                typeRole: 1,
                parentId: ''
            },
            errorInfo: {
                email: '',
                password: '',
                firstName: null,
                companyName: '',
                phone: null,
                typeRole: ''
            },
            isEdit: false,
            checkFormat: true,
            defaultPassword: 'ABC@91919wq',
            isLoading: false,
            checkPassword: false,
            checkSettingPassword: false,
            isLoadingResendEmail: false,
            isShowPassword: false,
            listRoles: [
                { value: 1, label: this.$t('member.roles.user') },
                { value: 2, label: this.$t('member.roles.admin') }
            ],
            listDataField: []
        };
    },
    computed: {
        ...mapState('auth', ['userInfo']),
        nameFullsize() {
            // eslint-disable-next-line no-irregular-whitespace
            return `${this.member.firstName ?? ''}${this.member.firstName ? '　' : ''}${this.member.lastName ?? ''}`;
        },
        nameHalfsize() {
            return `${this.member.firstName ?? ''}${this.member.firstName ? ' ' : ''}${this.member.lastName ?? ''}`;
        },
        fullname() {
            return `${this.member.firstName ?? ''}${this.member.lastName ?? ''}`;
        }
    },
    created() {
        let idQuery = this.$route.query.id || null;
        if (idQuery) {
            this.handleGetDetailMember(idQuery);
        }
    },
    methods: {
        resetPassword() {
            this.checkFormat = true;
        },
        onValidPhoneNumber(evt) {
            evt = evt || window.event;
            var charCode = evt.which ? evt.which : evt.keyCode;
            if (charCode > 31 && (charCode < 48 || charCode > 57)) {
                evt.preventDefault();
            } else {
                return true;
            }
        },
        onPasteValidPhoneNumber(evt) {
            let text = evt.clipboardData.getData('text');
            if (!validNumber(text)) {
                evt.preventDefault();
            } else {
                return true;
            }
        },
        checkPasswordChange() {
            if (this.checkPassword) {
                this.member.password = 'ABC@91919wq';
            } else {
                this.member.password = '';
            }
            this.onValidate('password');
        },
        handleGetDetailMember(id) {
            this.checkFormat = false;
            this.isEdit = true;
            this.isLoading = true;
            let successCallback = (response) => {
                let { data } = response.data.data;
                this.isLoading = false;
                let objData = {
                    firstName: data.surname,
                    lastName: data.name,
                    settingPassword: data['setting_password'] ? 1 : 0,
                    typeRole: data.type,
                    code: data.code,
                    phone: data['phone_number'],
                    note: data.note,
                    companyName: data['company_name'],
                    email: data.email,
                    id: data.id,
                    parentId: data['parent_id']
                };
                this.listDataField = data?.field;
                this.listDataField.forEach((item) => {
                    if (item.field_type === 'text') {
                        item.error_field_text = '';
                    }
                });
                this.checkSettingPassword = !!(data['setting_password'] && data['setting_password'] === 'true');
                this.member = Object.assign({}, objData);
            };
            let errorCallback = () => {
                this.isLoading = false;
            };
            let payload = {
                id: id,
                successCallback,
                errorCallback
            };
            this.$store.dispatch('member/getProfileMember', payload);
        },
        handleSaveData() {
            let check = this.onValidate();
            if (check) {
                let { isEdit, member, checkSettingPassword, checkFormat, listDataField } = this;
                if (this.isLoading) return;
                this.isLoading = true;
                let successCallback = () => {
                    this.$store.commit('noticeSystem/SET_MESSAGE', {
                        isShowMessage: true,
                        type: 'success',
                        message: isEdit ? this.$t('member.msgUpdateSuccess') : this.$t('member.msgAddSuccess')
                    });
                    this.handleGetDetailMember(member.id);
                    this.isLoading = false;
                };
                let errorCallback = () => {
                    this.isLoading = false;
                };
                let formData = new FormData();

                formData.append('surname', member.firstName || '');
                formData.append('name', member.lastName || '');
                formData.append('email', member.email.trim() || '');
                formData.append('type', member.typeRole);
                formData.append('company_name', member.companyName || '');
                formData.append('phone_number', member.phone || '');
                formData.append('note', member.note || '');
                listDataField.forEach((item, index) => {
                    formData.append('field[' + index + '][field_id]', listDataField[index].field_id);
                    formData.append('field[' + index + '][value]', listDataField[index].value ?? '');
                });
                if (isEdit) {
                    formData.append('id', member.id);
                    if (checkSettingPassword && checkFormat) {
                        formData.append('password', member.password);
                    }
                }
                let payload = {
                    data: formData,
                    successCallback,
                    errorCallback
                };
                this.$store.dispatch('member/updateProfileMember', payload);
            }
        },
        resendEmail() {
            this.$store.commit('noticeSystem/SET_CONFIRM', {
                type: `warning`,
                isShowConfirm: true,
                message: this.$t('member.resendEmailConfirm'),
                callback: () => {
                    if (this.isLoadingResendEmail) return;
                    this.isLoadingResendEmail = true;
                    let successCallback = () => {
                        this.$store.commit('noticeSystem/SET_MESSAGE', {
                            isShowMessage: true,
                            type: 'success',
                            message: this.$t('member.resendEmailSuccess')
                        });
                        this.isLoadingResendEmail = false;
                    };
                    let errorCallback = () => {
                        this.isLoadingResendEmail = false;
                    };

                    let payload = {
                        id: this.$route.query.id,
                        successCallback,
                        errorCallback
                    };
                    this.$store.dispatch('member/resendEmail', payload);
                }
            });
        },
        onValidate(field, item) {
            let check = true;
            let { member } = this;

            if (field === 'firstName' || !field) {
                if (!validRequired(member.firstName)) {
                    this.errorInfo.firstName = this.$t('member.msgFirstNameRequired');
                    check = false;
                } else if (!validMaxLength(member.firstName, 255)) {
                    this.errorInfo.firstName = this.$t('member.msgFirstNameMaxLength');
                    check = false;
                } else {
                    this.errorInfo.firstName = '';
                }
            }

            if (field === 'email' || !field) {
                if (!validRequired(member.email)) {
                    this.errorInfo.email = this.$t('member.msgEmailRequired');
                    check = false;
                } else if (!validEmail(member.email.trim())) {
                    this.errorInfo.email = this.$t('member.msgEmailFormat');
                    check = false;
                } else if (!validMaxLength(member.email.trim(), 255)) {
                    this.errorInfo.email = this.$t('member.msgEmailMaxLength');
                    check = false;
                } else {
                    this.errorInfo.email = '';
                }
            }

            if ((field === 'password' || !field) && this.isEdit && this.checkSettingPassword) {
                if (!validRequired(member.password) && this.checkFormat === true) {
                    this.errorInfo.password = this.$t('member.msgPasswordRequired');
                    check = false;
                } else if (member.password && !validDuplicator(member.password) && this.checkFormat) {
                    this.errorInfo.password = this.$t('member.msgPasswordSame');
                    check = false;
                } else if (member.password && (!validPassword(member.password) || member.password.length < 8 || member.password.length > 32) && this.checkFormat) {
                    this.errorInfo.password = this.$t('member.msgPasswordFormat');
                    check = false;
                } else {
                    this.errorInfo.password = '';
                }
            }

            if (field === 'company' || !field) {
                if (!validMaxLength(member.companyName, 255)) {
                    this.errorInfo.companyName = this.$t('member.msgCompanyMaxLength');
                    check = false;
                } else {
                    this.errorInfo.companyName = '';
                }
            }
            if (field === 'phone' || !field) {
                if (!validMaxLength(member.phone, 15)) {
                    this.errorInfo.phone = this.$t('member.msgPhoneMaxLength');
                    check = false;
                } else {
                    this.errorInfo.phone = '';
                }
            }

            if (field === 'note' || !field) {
                if (!validMaxLength(member.note, 2000)) {
                    this.errorInfo.note = this.$t('member.msgNoteMAxLength');
                    check = false;
                } else {
                    this.errorInfo.note = '';
                }
            }
            if (field === 'text_setting') {
                if (!validMaxLength(item.value, 250)) {
                    item.error_field_text = this.$t('text.max255', {
                        field: item.field_name
                    });
                    check = false;
                } else {
                    item.error_field_text = '';
                }
            }

            if (!field) {
                this.listDataField.forEach((value) => {
                    if (value.field_type === 'text') {
                        if (!validMaxLength(value.value, 250)) {
                            value.error_field_text = this.$t('text.max255', {
                                field: value.field_name
                            });
                            check = false;
                        } else {
                            value.error_field_text = '';
                        }
                    }
                });
            }

            return check;
        }
    }
};
</script>
